import React, { useState } from 'react';
import { Button, useNotify } from 'react-admin';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { entityDiscoveryService } from '../../services/entity-discovery.service';
import { getBaseUrl } from '../../framework/config/api.config';

export const SchemaDiscoveryButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const notify = useNotify();

  const handleDiscovery = async () => {
    setLoading(true);
    try {
      // Call the schema discovery endpoint
      const response = await fetch(`${getBaseUrl()}/entity-discovery/discover-schemas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Discovery failed: ${response.statusText}`);
      }

      const discoveryResult = await response.json();
      setResult(discoveryResult);
      
      notify(`Schema discovery completed: ${discoveryResult.totalEntities} entities processed`, { type: 'info' });
    } catch (error) {
      console.error('Schema discovery failed:', error);
      notify(`Schema discovery failed: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setResult(null);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        label="Discover Schemas"
        startIcon={<AutoFixHighIcon />}
        variant="contained"
        color="secondary"
      />
      
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AutoFixHighIcon sx={{ mr: 1 }} />
            Schema Discovery
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This will analyze all discovered entities and automatically create or update 
            schema definitions for core data models.
          </Typography>
          
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography>Discovering schemas from entities...</Typography>
            </Box>
          )}
          
          {result && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Discovery Results</Typography>
              <Typography>• Total Entities: {result.totalEntities}</Typography>
              <Typography>• Created: {result.created}</Typography>
              <Typography>• Updated: {result.updated}</Typography>
              <Typography>• Skipped: {result.skipped}</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {result.note}
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} label="Close" />
          {!loading && !result && (
            <Button
              onClick={handleDiscovery}
              label="Start Discovery"
              variant="contained"
              startIcon={<AutoFixHighIcon />}
            />
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
